export const RepositorySites = {
  namespaced: true,

  state: {
    repositorySites: {},
    pagination: null,
    saving: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    loading: false,
    loadedForms: false,
    loadedSites: false,
    loadedDbFields: false,
    loadedFormInstances: false,
    savingDbFields: false,
    repositorySitesForms: [],
    formInstances: [],
    settings: {},
    dbFields: [],
    search: null,
    forms: [],
  },

  getters: {
    getSites: (state) => (uuid) => {
      return state.repositorySites[uuid] ?? [];
    },

    getVisibleFields: (state) => state.dbFields.filter(({ visible }) => visible),

    isUserAllowedToCreate: ({ canAdd }) => canAdd,
    isUserAllowedToEdit: ({ canEdit }) => canEdit,
    isUserAllowedToDelete: ({ canDelete }) => canDelete,

    areSitesLoaded: (state) => (uuid) => {
      return Object.keys(state.repositorySites).includes(uuid);
    },

    getFormInstanceId:
      ({ formInstances }) =>
      (formId) => {
        const indexedFormInstances = formInstances.reduce((result, instance) => {
          return { ...result, [instance.form_id]: instance.instance_id };
        }, {});

        return indexedFormInstances[formId] ?? null;
      },

    hasInstances: ({ formInstances }) => formInstances && formInstances.length,

    areSitesLoading: (state) => state.loading,

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    loading: ({ loading }) => loading,

    loadedSites: ({ loadedSites }) => loadedSites,

    loadedForms: ({ loadedForms }) => loadedForms,

    loadedFormInstances: ({ loadedFormInstances }) => loadedFormInstances,

    forms: ({ forms }) => forms,

    repositorySitesSearch: ({ search }) => search,

    loadedDbFields: ({ loadedDbFields }) => loadedDbFields,

    dbFields: ({ dbFields }) => dbFields,

    savingDbFields: ({ savingDbFields }) => savingDbFields,
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setLoadedForms(state, value) {
      state.loadedForms = value;
    },

    setLoadedFormInstances(state, value) {
      state.loadedFormInstances = value;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    updateSites(state, newSite) {
      state.repositorySites = { ...state.repositorySites, ...newSite };
    },

    setUserCanAdd(state, allowed) {
      state.canAdd = allowed;
    },

    setUserCanEdit(state, allowed) {
      state.canEdit = allowed;
    },

    setUserCanDelete(state, allowed) {
      state.canDelete = allowed;
    },

    removeSite(state, removedSite) {
      let uuid = Object.keys(removedSite)[0];
      state.repositorySites[uuid] = state.repositorySites[uuid].filter((site) => site.uuid !== removedSite[uuid]);
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    setSettings(state, settings) {
      state.settings = settings;
    },

    setForms(state, forms) {
      state.forms = forms;
    },

    setFormInstances(state, formInstances) {
      state.formInstances = formInstances;
    },

    setSearch(state, search) {
      state.search = search;
    },

    clearFormInstances(state) {
      state.formInstances = [];
    },

    setLoadedDbFields(state, bool) {
      state.loadedDbFields = bool;
    },

    setLoadedSites(state, bool) {
      state.loadedSites = bool;
    },

    setDbFields(state, config) {
      state.dbFields = config;
    },

    setSavingDbFields(state, bool) {
      state.savingDbFields = bool;
    },
  },

  actions: {
    async loadSites({ commit }, request) {
      if (!request.repository) {
        return;
      }
      commit('setLoading', true);
      commit('setLoadedSites', false);
      let sites = {};
      let pagination = {};
      try {
        const response = await this._vm.$api(
          'repositories.sites.index',
          {
            ...request,
            version: '1.0',
          },
          {}
        );

        sites = response.data.data;
        pagination = { ...response.data.meta, ...response.data.links };
        commit('setUserCanAdd', response.data.can?.add);
        commit('setUserCanEdit', response.data.can?.edit);
        commit('setUserCanDelete', response.data.can?.delete);
      } catch (err) {
        console.error(err);
      }
      commit('updateSites', { [request.repository]: sites });
      commit('setPagination', { [request.repository]: pagination });
      commit('setLoading', false);
      commit('setLoadedSites', true);
    },

    async createSite(context, request) {
      const requestBody = request.site;
      const repository = request.repository;
      const metadata_instances = request.metadata_instances ?? {};

      context.commit('setSaving', true);

      try {
        await this._vm.$api(
          'repositories.sites.store',
          {
            version: '1.0',
            repository,
          },
          { basics: { ...requestBody }, metadata_instances }
        );
      } catch (err) {
        console.error(err);
      }

      context.commit('setSaving', false);
    },

    async updateSite(context, request) {
      const requestBody = request.body;
      const repository = request.repository;
      const site = request.site;

      context.commit('setLoading', true);

      try {
        await this._vm.$api(
          'repositories.sites.update',
          {
            version: '1.0',
            repository,
            site,
          },
          requestBody
        );
      } catch (err) {
        console.error(err);
      }

      context.commit('setLoading', false);
    },

    async deleteSite(context, request) {
      const site = request.site;
      const repository = request.repository;
      try {
        await this._vm.$api('repositories.sites.destroy', {
          version: '1.0',
          repository,
          site,
        });

        context.commit('removeSite', { [request.repository]: site });
      } catch (err) {
        console.error(err);
      }
    },

    async loadSettings({ commit }, { repository }) {
      commit('setLoading', true);

      try {
        const response = await this._vm.$api('repositories.site_settings.fetch', {
          version: '1.0',
          repository,
        });
        const settings = response?.data?.data ?? {};

        commit('setSettings', settings);
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async saveSettings({ commit }, { repositoryUuid, settings }) {
      commit('setLoading', true);

      try {
        await this._vm.$api('repositories.site_settings.save', { repository: repositoryUuid }, settings);

        commit('setSettings', settings);
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async loadForms({ commit }, repository) {
      commit('setLoadedForms', false);

      try {
        const response = await this._vm.$api('repositories.sites.forms', { repository });

        const formsData = response.data?.data ?? [];

        commit('setForms', formsData);
      } catch (err) {
        console.error(err);
      }
      commit('setLoadedForms', true);
    },

    async loadFormInstances({ commit }, { repositoryUuid, siteUuid }) {
      if (!repositoryUuid) {
        return;
      }

      commit('setLoadedFormInstances', false);

      try {
        let response = null;

        if (!siteUuid) {
          response = await this._vm.$api('repositories.sites.forms.instances', { repository: repositoryUuid });
        } else {
          response = await this._vm.$api('repositories.sites.forms.site_instances', {
            repository: repositoryUuid,
            site: siteUuid,
          });
        }

        const instancesData = response.data?.data ?? [];

        commit('setFormInstances', instancesData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoadedFormInstances', true);
    },

    async getDbFields({ commit }, repository) {
      commit('setLoadedDbFields', false);
      commit('setDbFields', []);

      try {
        const response = await this._vm.$api('repositories.site_settings_fields.fetch', {
          version: '1.0',
          repository,
        });

        const fieldsData = response.data?.data ?? [];

        commit('setDbFields', fieldsData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoadedDbFields', true);
    },

    async export(_, { repository, fields, filter }) {
      if (!repository) {
        return '';
      }

      const response = await this._vm.$api(
        'repositories.sites.export',
        { repository },
        {
          fields,
          filter: filter ?? {},
        }
      );

      return response.data?.data?.url ?? '';
    },

    async saveDbFields({ commit }, { repositoryUuid, fields }) {
      commit('setSavingDbFields', true);

      try {
        await this._vm.$api(
          'repositories.site_settings_fields.save',
          {
            repository: repositoryUuid,
          },
          { fields }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setSavingDbFields', false);
    },

    setRepositorySitesSearch({ commit }, search) {
      commit('setSearch', search);
    },

    clearFormInstances({ commit }) {
      commit('clearFormInstances');
    },
  },
};
